import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, FileText, Download, Eye, BookOpen, Check, 
  AlertTriangle, Droplets, Clock, ShieldCheck, Pill, Home, 
  Activity, Scissors, MessageCircle, ChevronDown, ChevronUp, Phone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card } from '../components/ui/card';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const GuiasRecursos = () => {
  const [openSection, setOpenSection] = React.useState(null);
  const lang = useLanguage();
  const t = getPageTexts('guiasRecursos', lang);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero */}
      <section className="pt-12 pb-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <p className="eyebrow mb-6">{t.heroEyebrow}</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">{t.title}</h1>
          <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
          <p className="text-text-secondary text-lg max-w-3xl mx-auto">{t.heroDesc}</p>
        </div>
      </section>

      {/* Guía de Blefaroplastia */}
      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          
          <div className="mb-10">
            <h2 className="font-serif text-3xl text-charcoal mb-3">{t.blepharoplasty}</h2>
            <div className="w-24 h-[3px] bg-gold"></div>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 border border-gold flex items-center justify-center bg-gold/10">
              <Eye className="text-gold" size={24} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-charcoal">{t.careGuide}</h3>
              <p className="text-gold text-sm">{t.preAndPost}</p>
            </div>
          </div>

          <p className="text-text-secondary mb-6">{t.careIntro}</p>

          <div className="mb-10">
            <Link to="/guia-cuidados-blefaroplastia" className="inline-flex items-center gap-3 bg-gold hover:bg-gold/90 text-white px-6 py-3 transition-all">
              <Download size={20} />
              <span>{t.downloadGuide}</span>
            </Link>
          </div>

          <div className="space-y-4">
            
            {/* 1. ANTES CON SEDACIÓN */}
            <Card className="overflow-hidden">
              <button onClick={() => toggleSection('antes-sedacion')} className="w-full p-5 flex items-center justify-between bg-charcoal text-white hover:bg-charcoal/90 transition-colors">
                <div className="flex items-center gap-3">
                  <Clock size={22} />
                  <span className="text-lg font-medium">{t.acc1Title}</span>
                </div>
                {openSection === 'antes-sedacion' ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
              </button>
              
              {openSection === 'antes-sedacion' && (
                <div className="p-6 space-y-5">
                  <div className="bg-amber-50 p-4 border-l-4 border-amber-500">
                    <p className="font-medium text-amber-800 mb-2">{t.fastingTitle}</p>
                    <p className="text-amber-700"><strong>{t.fastingDesc}</strong></p>
                  </div>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.fastingWater}</span></li>
                    <li className="flex items-start gap-3 bg-blue-50 p-3"><ShieldCheck className="text-blue-500 mt-1 flex-shrink-0" size={18} /><span><strong>{t.mustAccompanied}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.avoid24h}</span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.someoneNearby}</span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.personalPrep}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.comfyClothes}</span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.cleanFace}</span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.medications}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 bg-red-50 p-3"><AlertTriangle className="text-red-500 mt-1 flex-shrink-0" size={18} /><span><strong>{t.avoidNSAIDs}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.paracetamolOk}</span></li>
                  </ul>

                  <div className="bg-cream p-5 mt-6">
                    <h4 className="font-semibold text-charcoal mb-3">{t.itemsAtHome}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {t.items && t.items.map((item, i) => <span key={i}>• {item}</span>)}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* 2. ANTES SIN SEDACIÓN */}
            <Card className="overflow-hidden">
              <button onClick={() => toggleSection('antes-local')} className="w-full p-5 flex items-center justify-between bg-charcoal/80 text-white hover:bg-charcoal/70 transition-colors">
                <div className="flex items-center gap-3">
                  <Clock size={22} />
                  <span className="text-lg font-medium">{t.acc2Title}</span>
                </div>
                {openSection === 'antes-local' ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
              </button>
              
              {openSection === 'antes-local' && (
                <div className="p-6 space-y-5">
                  <div className="bg-green-50 p-4 border-l-4 border-green-500">
                    <p className="font-medium text-green-800 mb-2">{t.noFastingTitle}</p>
                    <p className="text-green-700">{t.noFastingDesc}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.arriveOnTime}</span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.informDoctor}</span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.personalPrep}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.comfyClothes}</span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.cleanFace}</span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.medications}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 bg-red-50 p-3"><AlertTriangle className="text-red-500 mt-1 flex-shrink-0" size={18} /><span><strong>{t.avoidNSAIDs}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.paracetamolOk}</span></li>
                  </ul>

                  <div className="bg-cream p-5 mt-6">
                    <h4 className="font-semibold text-charcoal mb-3">{t.itemsAtHome}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {t.items && t.items.map((item, i) => <span key={i}>• {item}</span>)}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* 3. DESPUÉS DEL PROCEDIMIENTO */}
            <Card className="overflow-hidden">
              <button onClick={() => toggleSection('despues')} className="w-full p-5 flex items-center justify-between bg-gold text-white hover:bg-gold/90 transition-colors">
                <div className="flex items-center gap-3">
                  <Activity size={22} />
                  <span className="text-lg font-medium">{t.acc3Title}</span>
                </div>
                {openSection === 'despues' ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
              </button>
              
              {openSection === 'despues' && (
                <div className="p-6 space-y-5">
                  <h4 className="font-semibold text-charcoal mb-3">{t.firstDays}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.coldCompress}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.sleepElevated}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.useTears}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.cleanGently}</span></li>
                    <li className="flex items-start gap-3 bg-blue-50 p-3"><ShieldCheck className="text-blue-500 mt-1 flex-shrink-0" size={18} /><span>{t.slightBleeding}</span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.medication}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.paracetamol8h}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.antibioticsIfPrescribed}</span></li>
                  </ul>

                  <div className="bg-red-50 p-5 border border-red-200 mt-6">
                    <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2"><AlertTriangle size={18} />{t.contactImmediately}</h4>
                    <ul className="space-y-1 text-red-700">
                      {t.urgentSymptoms && t.urgentSymptoms.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.followUp}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.dailyPhoto}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.stitchesRemoval}</strong></span></li>
                  </ul>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.avoidOneWeek}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {t.avoidItems && t.avoidItems.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 bg-amber-50 p-2 text-sm">
                        <AlertTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={14} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="font-semibold text-charcoal mt-6 mb-3">{t.scarCare}</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span><strong>{t.scarCream}</strong></span></li>
                    <li className="flex items-start gap-3"><Check className="text-gold mt-1 flex-shrink-0" size={18} /><span>{t.avoidSun}</span></li>
                  </ul>
                </div>
              )}
            </Card>

            {/* 4. CUIDADOS Y TRATAMIENTO DEL OJO SECO */}
            <Card className="overflow-hidden">
              <button onClick={() => toggleSection('ojo-seco')} className="w-full p-5 flex items-center justify-between bg-amber-600 text-white hover:bg-amber-500 transition-colors">
                <div className="flex items-center gap-3">
                  <Droplets size={22} />
                  <span className="text-lg font-medium">{t.acc4Title}</span>
                </div>
                {openSection === 'ojo-seco' ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
              </button>
              
              {openSection === 'ojo-seco' && (
                <div className="p-6 space-y-5">
                  <div className="bg-gold/10 p-5 border-l-4 border-gold">
                    <p className="text-text-secondary mb-3">{t.dryEyeIntro1}</p>
                    <p className="text-text-secondary">{t.dryEyeIntro2}</p>
                  </div>
                  <p className="text-text-secondary"><strong>{t.dryEyeSymptoms}</strong></p>

                  <div className="bg-blue-50 p-5 border-l-4 border-blue-500">
                    <h4 className="font-semibold text-charcoal mb-3">{t.whyOccurs}</h4>
                    <ul className="space-y-2">
                      {t.whyReasons && t.whyReasons.map((r, i) => (
                        <li key={i} className="flex items-start gap-2"><Check className="text-blue-500 mt-1 flex-shrink-0" size={16} /><span>{r}</span></li>
                      ))}
                    </ul>
                    <p className="text-blue-700 text-sm mt-3 font-medium">{t.notSerious}</p>
                  </div>

                  <h4 className="font-serif text-xl text-charcoal border-b-2 border-gold pb-2">{t.recommendedTreatment}</h4>

                  <div className="bg-cream p-5">
                    <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
                      <span className="bg-gold text-white w-7 h-7 rounded-full flex items-center justify-center text-sm">1</span>
                      {t.tears}
                    </h4>
                    <p className="text-text-secondary mb-3"><strong>{t.tearsDesc}</strong></p>
                    <p className="text-text-secondary mb-4">{t.tearsDuringDay}</p>
                    <p className="font-medium text-charcoal mb-2">{t.tearsOptions}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Hylo-Comod®</span>
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Thealoz Duo®</span>
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Artelac Rebalance®</span>
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Systane Ultra UD®</span>
                    </div>
                    <div className="bg-amber-50 p-3 border-l-4 border-amber-400 text-sm">
                      <p className="text-amber-800">{t.tearsTip}</p>
                    </div>
                  </div>

                  <div className="bg-cream p-5">
                    <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
                      <span className="bg-gold text-white w-7 h-7 rounded-full flex items-center justify-center text-sm">2</span>
                      {t.nightGel}
                    </h4>
                    <p className="text-text-secondary mb-3">{t.nightGelDesc}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-3">
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> VitA-POS®</span>
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Lubrithal®</span>
                      <span className="flex items-center gap-2"><Pill className="text-gold" size={14} /> Xailin Night®</span>
                    </div>
                    <p className="text-text-muted text-sm">{t.nightGelNote}</p>
                  </div>

                  <div className="bg-cream p-5">
                    <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
                      <span className="bg-gold text-white w-7 h-7 rounded-full flex items-center justify-center text-sm">3</span>
                      {t.rednessTitle}
                    </h4>
                    <p className="text-text-secondary mb-2">{t.rednessDesc}</p>
                    <div className="bg-red-50 p-3 border-l-4 border-red-400 text-sm">
                      <p className="text-red-700 font-medium">{t.rednessWarning}</p>
                    </div>
                  </div>

                  <h4 className="font-serif text-xl text-charcoal border-b-2 border-gold pb-2">{t.complementary}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {t.complementaryItems && t.complementaryItems.map((item, i) => (
                      <div key={i} className={`flex items-start gap-2 bg-green-50 p-3 ${i === t.complementaryItems.length - 1 ? 'md:col-span-2' : ''}`}>
                        <Check className="text-green-600 mt-0.5 flex-shrink-0" size={16} />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-50 p-4 border border-red-200">
                    <p className="text-red-700 text-sm font-medium">{t.avoidEyeDrops}</p>
                  </div>

                  <div className="bg-green-50 p-5 border-l-4 border-green-500">
                    <h4 className="font-semibold text-charcoal mb-3">{t.expectedEvolution}</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2"><Check className="text-green-600 mt-1 flex-shrink-0" size={16} /><span>{t.progressiveImprovement}</span></li>
                      <li className="flex items-start gap-2"><Check className="text-green-600 mt-1 flex-shrink-0" size={16} /><span><strong>{t.resolution8weeks}</strong></span></li>
                    </ul>
                  </div>

                  <div className="bg-red-50 p-5 border border-red-200">
                    <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2"><AlertTriangle size={18} />{t.consultUrgent}</h4>
                    <ul className="space-y-1 text-red-700">
                      {t.urgentEyeSymptoms && t.urgentEyeSymptoms.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>

                  <div className="bg-gold/10 p-4 text-center">
                    <p className="text-charcoal font-medium">{t.dryEyeReminder}</p>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Contact Card */}
          <Card className="p-6 mt-10 bg-charcoal text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">{t.anyQuestion}</h3>
                <p className="text-white/70 text-sm">{t.atYourService}</p>
              </div>
              <div className="flex gap-3">
                <a href="tel:+34952764547" className="inline-flex items-center gap-2 bg-ivory text-charcoal px-4 py-2 text-sm hover:bg-gray-100 transition-colors">
                  <Phone size={16} /> 952 76 45 47
                </a>
                <a href="https://wa.me/34670506262" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold text-white px-4 py-2 text-sm hover:bg-gold/90 transition-colors">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </Card>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-amber-700"><strong>{t.disclaimerNote}</strong> {t.disclaimerText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Próximamente */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <p className="eyebrow mb-4 text-center">{t.inDevelopment}</p>
          <h2 className="font-serif text-2xl text-charcoal text-center mb-8">{t.upcomingGuides}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {t.upcomingList && t.upcomingList.map((guia, index) => (
              <div key={index} className="p-4 bg-ivory border border-divider text-center">
                <FileText className="text-text-muted mx-auto mb-2" size={20} />
                <p className="text-text-muted text-sm">{guia}</p>
                <span className="inline-block mt-2 text-[9px] tracking-wider uppercase bg-divider text-text-muted px-2 py-0.5">{t.comingSoon}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-2xl text-ivory mb-4">{t.needMoreInfo}</h2>
          <p className="text-gold-light/70 mb-8">{t.dontHesitate}</p>
          <Link to="/contacto" className="btn-gold inline-flex items-center gap-2">
            {t.contact} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default GuiasRecursos;
